import axios from 'axios';
import { getWmsClientes } from './api.wms.clientes';
import { getWmsProductos } from './api.wms.productos';

export const wmsApi = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

wmsApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Crear almacén WMS
export const createWmsAlmacen = async (almacenData) => {
  const response = await wmsApi.post('/api/v1/wms/bodegas/', almacenData);
  return response.data;
};

// Obtener almacenes desde el endpoint WMS
export const getWmsAlmacenes = async () => {
  const response = await wmsApi.get('/api/v1/wms/bodegas');
  return response.data;
};

// Obtener ubicaciones WMS reales
export const getWmsUbicaciones = async () => {
  const response = await wmsApi.get('/api/v1/wms/ubicaciones');
  return response.data;
};

// Obtener ubicaciones de la tabla ubicaciones (destinos para órdenes de salida)
export const getUbicaciones = async () => {
  const response = await wmsApi.get('/api/v1/wms/destinos');
  return response.data;
};

// Crear nueva ubicación (destino)
export const createUbicacion = async (ubicacionData) => {
  const response = await wmsApi.post('/api/v1/wms/destinos', ubicacionData);
  return response.data;
};

// Crear ubicación WMS
export const createWmsUbicacion = async (ubicacionData) => {
  const response = await wmsApi.post('/api/v1/wms/ubicaciones', ubicacionData);
  return response.data;
};

// Actualizar almacén WMS
export const updateWmsAlmacen = async (id, almacenData) => {
  const response = await wmsApi.put(`/api/v1/wms/bodegas/${id}`, almacenData);
  return response.data;
};

// Obtener pasillos de un almacén
export const getWmsPasillosByAlmacen = async (almacenId) => {
  const response = await wmsApi.get(`/api/v1/wms/pasillos?almacen_id=${almacenId}`);
  return response.data;
};

// Crear pasillo WMS
export const createWmsPasillo = async (pasilloData) => {
  const response = await wmsApi.post('/api/v1/wms/pasillos', pasilloData);
  return response.data;
};

// Obtener almacén por ID
export const getWmsAlmacenById = async (id) => {
  const response = await wmsApi.get(`/api/v1/wms/bodegas/${id}`);
  return response.data;
};

// Actualizar ubicación WMS
export const updateWmsUbicacion = async (id, ubicacionData) => {
  const response = await wmsApi.put(`/api/v1/wms/ubicaciones/${id}`, ubicacionData);
  return response.data;
};

// Eliminar ubicación WMS
export const deleteWmsUbicacion = async (id) => {
  const response = await wmsApi.delete(`/api/v1/wms/ubicaciones/${id}`);
  return response.data;
};

// Edición masiva de ubicaciones (por rack/base)
export const editarUbicacionesMasiva = async ({ pasillo_id, lado, ubicacion, niveles, posiciones }) => {
  const response = await wmsApi.post('/api/v1/wms/ubicaciones/edicion-masiva', {
    pasillo_id,
    lado,
    ubicacion,
    niveles,
    posiciones
  });
  return response.data;
};

// Obtener previsualización de edición masiva de ubicaciones (GET)
export const previewEditarUbicacionesMasiva = async ({ pasillo_id, lado, ubicacion, niveles, posiciones }) => {
  const params = new URLSearchParams({
    pasillo_id, lado, ubicacion, niveles, posiciones
  });
  const response = await wmsApi.get(`/api/v1/wms/ubicaciones/edicion-masiva-preview?${params.toString()}`);
  return response.data;
};

// Crear ubicaciones WMS en batch
export const createWmsUbicacionesBatch = async (ubicacionesData) => {
  const response = await wmsApi.post('/api/v1/wms/ubicaciones/batch', ubicacionesData);
  return response.data;
};

// Actualizar pasillo WMS
export const updateWmsPasillo = async (id, pasilloData) => {
  const response = await wmsApi.put(`/api/v1/wms/pasillos/${id}`, pasilloData);
  return response.data;
};

// Imprimir etiqueta ZPL en impresora Zebra
export const printZPL = async ({ zpl, printer_ip, printer_port = 9100 }) => {
  const response = await wmsApi.post('/print/zpl', { zpl, printer_ip, printer_port });
  return response.data;
};

// Usuarios WMS (actualizado para usar endpoints correctos de autenticación)
export const getWmsUsers = async () => {
  const response = await wmsApi.get('/api/v1/auth/users');
  return response.data;
};
export const createWmsUser = async (userData) => {
  const response = await wmsApi.post('/api/v1/auth/register', userData);
  return response.data;
};
export const updateWmsUser = async (id, userData) => {
  const response = await wmsApi.put(`/api/v1/auth/users/${id}`, userData);
  return response.data;
};
export const deleteWmsUser = async (id) => {
  const response = await wmsApi.delete(`/api/v1/auth/users/${id}`);
  return response.data;
};

// Obtener almacenes asignados a un usuario WMS
export const getWarehousesByUser = async (userId) => {
  const response = await wmsApi.get(`/api/v1/wms/user/${userId}/warehouses`);
  return response.data; // Se espera un array de IDs de almacenes
};

// Asignar almacenes a un usuario WMS
export const assignWarehousesToUser = async (userId, warehouseIds) => {
  const response = await wmsApi.post(`/api/v1/wms/user/${userId}/warehouses`, { warehouse_ids: warehouseIds });
  return response.data;
};

// Obtener usuarios WMS con almacenes (solo id y nombre)
export const getWmsUsuariosWithWarehouses = async () => {
  const response = await wmsApi.get('/api/v1/wms/usuarios-wms');
  return response.data;
};

// Obtener impresoras de un almacén
export const getWmsImpresorasByAlmacen = async (almacenId) => {
  const response = await wmsApi.get(`/impresoras?almacen_id=${almacenId}`);
  return response.data;
};

// Crear impresora
export const createWmsImpresora = async (impresoraData) => {
  const response = await wmsApi.post('/impresoras', impresoraData);
  return response.data;
};

// Actualizar impresora
export const updateWmsImpresora = async (id, impresoraData) => {
  const response = await wmsApi.put(`/impresoras/${id}`, impresoraData);
  return response.data;
};

// Eliminar impresora
export const deleteWmsImpresora = async (id) => {
  const response = await wmsApi.delete(`/impresoras/${id}`);
  return response.data;
};

// Crear orden de entrada WMS
export const createWmsOrden = async (ordenData) => {
  const response = await wmsApi.post('/api/v1/wms/ordenes/', ordenData);
  return response.data;
};

// Obtener todas las órdenes WMS
export const getWmsOrdenes = async () => {
  const response = await wmsApi.get('/api/v1/wms/ordenes/');
  return response.data;
};

// Obtener orden de entrada por iddocumento
export const getWmsOrdenByIdDocumento = async (iddocumento) => {
  const response = await wmsApi.get(`/api/v1/wms/ordenes/?iddocumento=${iddocumento}`);
  // Suponiendo que el backend filtra por iddocumento y devuelve un array
  if (Array.isArray(response.data) && response.data.length > 0) {
    return response.data[0];
  }
  return null;
};

// Cambiar estatus de orden de entrada WMS
export const updateWmsOrdenEstatus = async (ordenId, estatus) => {
  const response = await wmsApi.patch(`/api/v1/wms/ordenes/${ordenId}/estatus`, { estatus });
  return response.data;
};

// Afectar productos al inventario (ingreso)
export const ingresarWmsInventario = async (movimientos) => {
  // movimientos: array de { producto_id, cantidad, ubicacion_id, lote, fecha_ingreso, ... }
  const response = await wmsApi.post('/api/v1/wms/inventario/ingreso', movimientos);
  return response.data;
};

// Funciones para obtener almacenes, clientes y productos para formularios de órdenes WMS
export const obtenerAlmacenes = getWmsAlmacenes;
export const obtenerClientes = getWmsClientes;
export const obtenerProductos = getWmsProductos;
// Crear orden (entrada/salida)
export const crearOrden = createWmsOrden;

// Buscar ubicación WMS por código
export const getWmsUbicacionByCodigo = async (codigo) => {
  const response = await wmsApi.get(`/api/v1/wms/ubicaciones/codigo/${codigo}`);
  return response.data;
};

// Obtener roles desde el backend
export const getRoles = async () => {
  const response = await wmsApi.get('/api/v1/roles');
  return response.data;
};

// Obtener nombres de roles OMS desde el backend para selectsystem
export const getRoleNames = async () => {
  const response = await wmsApi.get('/api/v1/roles/names');
  return response.data;
};

// Puedes agregar aquí más funciones WMS específicas en el futuro.
